<template>
  <div class="categories">
    <ImprovedCustomDropdown
      v-for="(_selected, i) in nrShownCategoryDropdowns"
      :key="i"
      class="category value"
      :class="[
        indexedChanges[i] || hasChangedStatus ? 'modified-bg modified' : 'bg-white default-border',
        {
          'mx-0': discreetInputs,
        },
      ]"
      :inner-class="`${discreetInputs ? 'p-1' : ''} ${modelValueOptions[i] && !modelValueOptions[i]?.staged ? '' : 'staged'}`"
      :options="hierarchicalOptions[i] || []"
      :placeholder="placeholder || translate('Category %s', 'attributes', 'capitalize', undefined, [i + 1])"
      :allow-custom-values="allowCustomValues ? 'confirm' : false"
      :discreet-inputs="discreetInputs"
      :model-value="modelValue?.[i] ? [modelValue?.[i]] : []"
      :max-length="maxLength"
      @new="emit('new', { ...$event, parentId: selectedIds[i - 1] ?? modelValue[i - 1]?.toString().toLowerCase() })"
      @hide-staged="emit('hide-staged', $event)"
      @input="emit('input', $event)"
      @select="handleUpdate($event.value.value, i)"
      @keydown.exact.tab="i < nrShownCategoryDropdowns - 1 ? $event.stopPropagation() : 0"
      @keydown.shift.tab="i !== 0 ? $event.stopPropagation() : 0"
    />
    <!-- @remove="emit('remove', $event), spliceHierarchy(i)" -->
  </div>
</template>

<script lang="ts" setup>
import ImprovedCustomDropdown from '@/components/ImprovedCustomDropdown.vue'
import { useTranslation } from '@/composables/useTranslation'
import { HierarchicalOption } from '@/models/Global'
import { SimplifiedCategory } from '@/models/Taxonomy'
import { useTaxonomiesStore } from '@/store/taxonomiesStore'
import { areComparable, clone, toComparable } from '@/utils/tools'
import { computed } from 'vue'

const props = defineProps<{
  original?: SimplifiedCategory[]
  modelValue: Array<string | number>
  discreetInputs?: boolean
  placeholder?: string
  hasChangedStatus?: boolean
  options?: HierarchicalOption<string | number>[]
  maxLength?: number
  allowCustomValues?: boolean
}>()

const { translate } = useTranslation()
const taxonomiesStore = useTaxonomiesStore()

// ---------------
// -- Reactives --
// ---------------
const emit = defineEmits(['update:modelValue', 'input', 'add', 'remove', 'new', 'hide-staged'])

// --------------------
// -- Computed props --
// --------------------
const nrShownCategoryDropdowns = computed(() => Math.min(props.modelValue?.length + 1, 3))

const selectedIds = computed(() => {
  let parentId: string | null | undefined = undefined

  return props.modelValue.map((categoryDefaultName) => {
    const found = taxonomiesStore.findCategory(categoryDefaultName, parentId)
    parentId = found?.id || null
    return parentId
  })
})

/**
 * Modelvalue to options
 */
const modelValueOptions = computed(() => {
  return props.modelValue.map((categoryDefaultName, i) => {
    return hierarchicalOptions.value[i]?.find((o) => areComparable(o.value, categoryDefaultName))
  })
})

const hierarchicalOptions = computed(() => {
  return [...selectedIds.value, ''].map((_, i) => {
    const parentId = selectedIds.value[i - 1]
    return (
      props.options?.filter((option) => {
        return option.parentId === parentId || option.parentId === props.modelValue[i - 1] // staged options uses name as parentId
      }) || []
    )
  })
})

const indexedChanges = computed(() =>
  Array.from({ length: 3 }).map((_, i) => {
    const current = props.modelValue.slice(0, i + 1).join(',')

    const original = props.original
      ?.slice(0, i + 1)
      .map((c) => c.defaultName)
      .join(',')

    return current !== original && (props.modelValue[i] || props.original?.[i])
  }, []),
)

// ---------------
// -- Functions --
// ---------------
const handleUpdate = (updatedValue: string | number | undefined, updatedIndex: number) => {
  if (updatedValue) {
    const modelClone = clone(props.modelValue)
    modelClone[updatedIndex] = updatedValue
    emit('update:modelValue', modelClone)
  }
}

const spliceHierarchy = (index: number) => {
  emit(
    'update:modelValue',
    props.modelValue.filter((_, i) => i !== index),
  )
}
</script>

<style lang="scss" scoped>
.form-group {
  margin: 0;
}

.categories {
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  background-color: white;

  .category {
    margin: 0.1em 0;

    &.default-border {
      outline: none;
      border-color: #ced4da !important; // same as form-control
    }
  }
}
</style>
